// src/pages/mission/MissionService.ts

import { missionApiClient } from "../../services/apiClient";

// On définit un type qui reflète exactement votre enum Java
export type MissionStatus =
    | "BROUILLON"
    | "PUBLIEE"
    | "EN_ATTENTE"
    | "EN_COURS"
    | "TERMINEE"
    | "ANNULEE";

// Interface DTO de réponse
export interface Mission {
    id: string;
    commercialId: string;
    opportunite: string;
    description: string;
    businessDeveloperSub: string;
    status: MissionStatus;
    publicationDate: string;
    compte: string;
    domaine: string;
    metier: string;
    experience: string | null;
    manager: string;
    managerEmail: string;
    localisation: string;
}

// Payload de création attendu par l'API
export interface CreateMissionPayload {
    commercialId: string;
    opportunite: string;
    description: string;
    businessDeveloperSub: string;
    status: MissionStatus;
    manager: string;
    managerEmail: string;
    localisation: string;
    experience: string | null;
}

export interface ApplyToMissionPayload {
    commercialId: string;
    opportunite: string;
    description: string;
    businessDeveloperSub: string;
    status: MissionStatus;
    manager: string;
}

// Payload de mise à jour
export interface UpdateMissionPayload {
    opportunite?: string;
    description?: string;
    status: MissionStatus;
    businessDeveloperSub: string;
}

/**
 * Récupère la liste de toutes les missions.
 */
export async function getMissions(): Promise<Mission[]> {
    const response = await missionApiClient.get<Mission[]>("/missions");
    return response.data;
}

/**
 * Récupère une mission spécifique par son ID.
 * @param missionId - L’ID de la mission.
 */
export async function getMissionById(missionId: string): Promise<Mission> {
    const response = await missionApiClient.get<Mission>(`/missions/${missionId}`);
    return response.data;
}

/**
 * Crée une nouvelle mission.
 */
export async function createMission(
    payload: CreateMissionPayload
): Promise<Mission> {
    const requestBody = {
        commercialId: payload.commercialId,
        opportunite: payload.opportunite,
        description: payload.description,
        businessDeveloperSub: payload.businessDeveloperSub,
        status: payload.status,
        manager: payload.manager,
        managerEmail: payload.managerEmail
    };

    const response = await missionApiClient.post<Mission>(
        "/missions",
        requestBody
    );
    return response.data;
}

/**
 * Met à jour une mission existante (opportunite, description, statut et businessDeveloperSub).
 */
export async function updateMission(
    missionId: string,
    payload: UpdateMissionPayload & { manager?: string } // Manager optionnel pour mise à jour
): Promise<Mission> {
    const response = await missionApiClient.put<Mission>(
        `/missions/${missionId}`,
        payload
    );
    return response.data;
}

/**
 * Supprime une mission existante.
 * @param missionId - L’ID de la mission à supprimer.
 */
export async function deleteMission(
    missionId: string
): Promise<void> {
    await missionApiClient.delete<void>(`/missions/${missionId}`);
}
